import { useEffect, useMemo, useState } from 'react';
import {
  Calculator,
  Check,
  Clock3,
  Copy,
  FolderClock,
  MoreHorizontal,
  Package,
  PackagePlus,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
  WalletCards,
} from 'lucide-react';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  catalogProductFromEstimate,
  catalogProductIdForEstimate,
  findCatalogDuplicate,
  normalizeCatalogValue,
} from '../lib/catalogProduct';
import ConfirmDialog from './ConfirmDialog';

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function timestampValue(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  return Number(value) || 0;
}

function readableDate(value) {
  const milliseconds = timestampValue(value);
  if (!milliseconds) return 'Just now';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(milliseconds));
}

function readableError(error) {
  const code = error?.code || '';
  if (code.includes('permission-denied')) return 'Firestore blocked access to your saved estimates. Confirm your user subcollection rules are published.';
  if (code.includes('unavailable')) return 'MakerMargin could not reach Firebase. Check your connection and try again.';
  return error?.message || 'Saved estimates could not be loaded.';
}

export default function SavedEstimates({ user, onRequestAuth, onOpen, onNew }) {
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(Boolean(user));
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [openMenu, setOpenMenu] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [working, setWorking] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (!user) {
      setEstimates([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const reference = collection(db, 'users', user.uid, 'estimates');
    return onSnapshot(reference, (snapshot) => {
      const items = snapshot.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .sort((a, b) => timestampValue(b.updatedAt) - timestampValue(a.updatedAt));
      setEstimates(items);
      setLoading(false);
    }, (snapshotError) => {
      setError(readableError(snapshotError));
      setLoading(false);
    });
  }, [user]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(''), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const visibleEstimates = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return estimates;
    return estimates.filter((estimate) => [
      estimate.name,
      estimate.profileName,
      estimate.profileSnapshot?.materialType,
      estimate.source?.name,
      estimate.source?.detectedSlicer,
    ].some((value) => String(value || '').toLowerCase().includes(needle)));
  }, [estimates, query]);

  const duplicateEstimate = async (estimate) => {
    if (!user || !db) return;
    setOpenMenu(null);
    setWorking(true);
    try {
      const reference = doc(collection(db, 'users', user.uid, 'estimates'));
      const copy = { ...estimate };
      delete copy.id;
      delete copy.createdAt;
      delete copy.updatedAt;
      await setDoc(reference, {
        ...copy,
        name: `${estimate.name || 'Untitled estimate'} Copy`,
        ownerId: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setToast('Estimate duplicated.');
    } catch (duplicateError) {
      setError(readableError(duplicateError));
    } finally {
      setWorking(false);
    }
  };

  const addEstimateToCatalog = async (estimate) => {
    if (!estimate?.id || !user || !db) return;
    setOpenMenu(null);
    setWorking(true);
    setError('');
    try {
      const productValues = catalogProductFromEstimate(estimate);
      const productsSnapshot = await getDocs(collection(db, 'users', user.uid, 'products'));
      const products = productsSnapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
      const sourceProduct = products.find((product) => product.sourceEstimateId === estimate.id) || null;
      const duplicate = findCatalogDuplicate(products, productValues, sourceProduct?.id);
      if (duplicate) {
        throw new Error(`A catalog product named ${duplicate.name || productValues.name} already exists. Rename the estimate or update that catalog product instead.`);
      }
      const reference = doc(db, 'users', user.uid, 'products', sourceProduct?.id || catalogProductIdForEstimate(estimate.id));
      const existingProduct = sourceProduct ? null : await getDoc(reference);
      const productExists = Boolean(sourceProduct || existingProduct?.exists());
      await setDoc(reference, {
        ...productValues,
        normalizedName: normalizeCatalogValue(productValues.name),
        ownerId: user.uid,
        ...(!productExists ? {
          sku: '',
          normalizedSku: '',
          category: 'Quoted work',
          notes: '',
          createdAt: serverTimestamp(),
        } : {}),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setToast(productExists ? 'Catalog product updated from this estimate.' : 'Estimate added to the product catalog.');
    } catch (catalogError) {
      setError(readableError(catalogError));
    } finally {
      setWorking(false);
    }
  };

  const deleteEstimate = async () => {
    if (!deleteTarget || !user || !db) return;
    setWorking(true);
    try {
      await deleteDoc(doc(db, 'users', user.uid, 'estimates', deleteTarget.id));
      setDeleteTarget(null);
      setToast('Estimate deleted.');
    } catch (deleteError) {
      setError(readableError(deleteError));
    } finally {
      setWorking(false);
    }
  };

  if (!user) {
    return (
      <section className="signed-out-state brushed-panel">
        <span className="signed-out-state__icon"><FolderClock size={30} /></span>
        <p className="eyebrow">Saved pricing library</p>
        <h1>Sign in to reopen your estimates.</h1>
        <p>Saved calculations stay attached to your MakerMargin account so you can update filament, electricity, labor, and margin assumptions later.</p>
        <div className="signed-out-state__actions">
          <button className="button button--primary button--large" type="button" onClick={() => onRequestAuth('signup')}>Create account</button>
          <button className="button button--ghost button--large" type="button" onClick={() => onRequestAuth('signin')}>Sign in</button>
        </div>
      </section>
    );
  }

  return (
    <section className="estimates-page">
      <header className="estimates-page__hero">
        <div>
          <p className="eyebrow">Phase 4 · Saved estimates</p>
          <h1>Reopen the math, not a memory.</h1>
          <p>Every saved estimate keeps the job inputs, selected profile snapshot, result, and last-updated time togetherâ€”and can become a reusable catalog product.</p>
        </div>
        <button className="button button--primary button--large" type="button" onClick={onNew}><Plus size={19} /> New estimate</button>
      </header>

      {error && <div className="config-banner config-banner--error" role="alert"><ShieldCheck size={18} /><span>{error}</span><button type="button" onClick={() => setError('')}>Dismiss</button></div>}

      <section className="estimates-toolbar brushed-panel">
        <label className="estimates-search">
          <Search size={17} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search estimates, profiles, or materials" />
        </label>
        <span>{visibleEstimates.length} of {estimates.length} estimate{estimates.length === 1 ? '' : 's'}</span>
      </section>

      <section className="estimates-library brushed-panel">
        {loading ? (
          <div className="estimates-loading" aria-live="polite"><span /><span /><span /><p>Loading saved estimates…</p></div>
        ) : estimates.length === 0 ? (
          <div className="profiles-empty">
            <span><Calculator size={28} /></span>
            <h3>Your saved pricing will live here.</h3>
            <p>Build an estimate, save it to Firebase, and reopen it whenever costs or quantities change.</p>
            <button className="button button--primary" type="button" onClick={onNew}><Plus size={18} /> Create first estimate</button>
          </div>
        ) : visibleEstimates.length === 0 ? (
          <div className="profiles-empty">
            <span><Search size={28} /></span>
            <h3>No estimates match that search.</h3>
            <p>Try a product name, material, or cost profile.</p>
          </div>
        ) : (
          <div className="estimate-card-grid">
            {visibleEstimates.map((estimate) => {
              const inputs = estimate.inputs || {};
              const results = estimate.results || {};
              return (
                <article className="estimate-card" key={estimate.id}>
                  <div className="estimate-card__top">
                    <span className="estimate-card__icon"><Package size={20} /></span>
                    <div><h2>{estimate.name || 'Untitled estimate'}</h2><p>{estimate.profileName || 'Saved cost profile'}{estimate.source?.name ? ` · ${estimate.source.name}` : ''}</p></div>
                    <div className="profile-card__menu-wrap">
                      <button className="icon-button profile-card__menu" type="button" onClick={() => setOpenMenu(openMenu === estimate.id ? null : estimate.id)} aria-label={`Actions for ${estimate.name || 'estimate'}`} aria-expanded={openMenu === estimate.id}><MoreHorizontal size={18} /></button>
                      {openMenu === estimate.id && (
                        <div className="profile-menu">
                          <button type="button" onClick={() => onOpen(estimate.id)}><WalletCards size={16} /> Open estimate</button>
                          <button type="button" onClick={() => addEstimateToCatalog(estimate)} disabled={working}><PackagePlus size={16} /> Add to catalog</button>
                          <button type="button" onClick={() => duplicateEstimate(estimate)} disabled={working}><Copy size={16} /> Duplicate</button>
                          <button className="profile-menu__danger" type="button" onClick={() => { setDeleteTarget(estimate); setOpenMenu(null); }}><Trash2 size={16} /> Delete</button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="estimate-card__price">
                    <small>Recommended per piece</small>
                    <strong>{currency.format(Number(results.recommendedPricePerPiece || 0))}</strong>
                    <span>{Number(inputs.targetMargin || results.targetMargin || 0).toFixed(1)}% target margin</span>
                  </div>

                  <div className="estimate-card__metrics">
                    <span><small>Quantity</small><strong>{Number(inputs.quantity || results.quantity || 1)}</strong></span>
                    <span><small>Batch cost</small><strong>{currency.format(Number(results.totalBatchCost || 0))}</strong></span>
                    <span><small>Batch price</small><strong>{currency.format(Number(results.recommendedBatchPrice || 0))}</strong></span>
                  </div>

                  <footer className="estimate-card__footer">
                    <span><Clock3 size={14} /> Updated {readableDate(estimate.updatedAt)}</span>
                    <div className="estimate-card__footer-actions">
                      <button type="button" onClick={() => addEstimateToCatalog(estimate)} disabled={working}><PackagePlus size={15} /> Add to catalog</button>
                      <button type="button" onClick={() => onOpen(estimate.id)}>Open <WalletCards size={15} /></button>
                    </div>
                  </footer>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {deleteTarget && <ConfirmDialog title={`Delete ${deleteTarget.name || 'this estimate'}?`} message="This permanently removes the saved calculation. Your cost profile and other estimates are not affected." working={working} onCancel={() => !working && setDeleteTarget(null)} onConfirm={deleteEstimate} />}
      {toast && <div className="toast" role="status"><Check size={17} /> {toast}</div>}
    </section>
  );
}
